import {
  useState,
  forwardRef,
  ReactNode,
  ButtonHTMLAttributes,
  MouseEvent,
} from "react";
import styled, { css, keyframes } from "styled-components/macro";
import { NavLink } from "react-router-dom";
import Overlay, { OverlayContent, OverlayTrigger } from "./Overlay";
import { Icon } from "./Icons";
import { BoxContent } from "./Box";
import { THEME_COLOR } from "../vars";

export const Menu = styled.div`
  ${BoxContent}>& {
    margin-left: -20px;
    margin-right: -20px;
    &:first-child {
      margin-top: -20px;
    }
    &:last-child {
      margin-bottom: -20px;
    }
  }
`;

const Spinner = styled.div`
  flex-basis: auto;
  flex-grow: 0;
  flex-shrink: 1;
  height: 17px;
  width: 16px;
  position: relative;
  margin-right: 8px;
  margin-left: -8px;
  &:last-child {
    margin-right: 0;
    margin-left: 8px;
  }
`;

const loaderKeyframes = keyframes`
  from {
    transform: translate(-50%, -50%) rotate(0deg);
  }
  to {
    transform: translate(-50%, -50%) rotate(360deg);
  }
`;

const LoaderCircle = styled.div`
  display: block;
  position: absolute;
  top: 50%;
  left: 50%;
  border-radius: 10000px;
  height: 16px;
  width: 16px;
  border: 2px solid transparent;
  border-left-color: ${THEME_COLOR};
  animation: 1s ${loaderKeyframes} linear infinite;
`;

export const MenuBadge = styled.div`
  display: inline-flex;
  background: #f9cc19;
  color: #0a344a;
  border-radius: 1000px;
  font-size: 10px;
  justify-content: center;
  align-items: center;
  min-width: 18px;
  padding: 0 8px;
  height: 18px;
  margin-left: 8px;
  overflow: hidden;
`;

export const MenuItemOptions = styled.div`
  display: inline-flex;
  justify-content: center;
  align-items: center;
  justify-content: flex-end;
  display: flex;
  margin-right: -12px;
  padding: 2px 0;
  flex-grow: 0;
  flex-shrink: 0;
  & > * {
    margin-left: 8px;
  }
`;

const commonCss = css<{ allowwrap?: boolean }>`
  display: flex;
  flex-direction: row;
  padding: 0 20px;
  min-height: 40px;
  align-items: center;
  justify-content: space-between;
  border: 0;
  background: transparent;
  font-size: 14px;
  width: 100%;
  text-align: inherit;
  position: relative;
  line-height: 1.8em;
  ${(p) =>
    !p.allowwrap &&
    css`
      & > *:not(${Spinner}):not(${MenuItemOptions}):not(${MenuBadge}):not(svg) {
        flex-basis: auto;
        flex-grow: 1;
        flex-shrink: 1;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow-x: hidden;
      }
    `}
  & > ${MenuBadge} {
    flex-grow: 0;
    flex-shrink: 0;
    flex-basis: auto;
  }
  & > ${Icon} {
    height: 12px;
    width: 12px;
    position: relative;
    color: ${THEME_COLOR};
    flex-basis: auto;
    flex-grow: 0;
    flex-shrink: 0;
    transform: scale(1.15) translateX(-8px);
    padding: 0;
  }
`;

export const MenuItem = styled.div`
  ${commonCss}
`;

export const MenuLabel = styled(MenuItem)`
  color: #888;
  text-transform: uppercase;
  font-weight: 500;
  font-size: 0.8em;
  min-height: unset;
  margin-top: 8px;
  padding: 0 10px;
`;

const interactiveMenuItemCss = css`
  ${commonCss}
  color: #474344;
  transition: 0.5s;
  &:not([disabled]) {
    cursor: pointer;
    &:hover {
      background: rgba(0, 0, 0, 0.03);
      color: #474344;
      transition: 0.2s;
    }
    &:active {
      background: rgba(0, 0, 0, 0.08);
      color: #474344;
    }
    &:link {
      text-decoration: none;
    }
  }
  &.active.active {
    background: ${THEME_COLOR};
    color: white;
    ${MenuBadge} {
      color: ${THEME_COLOR};
      background: white;
    }
  }
  &[disabled] {
    opacity: 0.5;
  }
`;

const InteractiveMenuItem = styled(MenuItem)`
  ${interactiveMenuItemCss}
`;

export const MenuSeparator = styled.div`
  height: 1px;
  width: 100%;
  background-color: rgb(229, 229, 229);
`;

export const MenuLinkItem = styled(NavLink)`
  ${interactiveMenuItemCss}
`;
export const MenuRawLinkItem = styled.a`
  ${interactiveMenuItemCss}
`;
export const MenuButtonItemContainer = styled(InteractiveMenuItem)``;

const MenuButtonItem = styled(
  forwardRef<
    HTMLButtonElement,
    {
      icon?: ReactNode;
      badge?: ReactNode;
      onClick: (e: MouseEvent) => void | Promise<void>;
      disabled?: boolean;
      ignore?: boolean;
    } & Omit<ButtonHTMLAttributes<HTMLButtonElement>, "onClick" | "disabled">
  >(function MenuButtonItem(
    {
      icon,
      badge,
      children,
      onClick: givenOnClick,
      disabled,
      ignore,
      ...props
    },
    ref
  ) {
    const [isLoading, setIsLoading] = useState(false);

    const onClick = async (e: MouseEvent<HTMLButtonElement>) => {
      setIsLoading(true);
      try {
        await givenOnClick(e);
      } finally {
        setIsLoading(false);
      }
    };

    return (
      <MenuButtonItemContainer
        as="button"
        onClick={onClick}
        disabled={disabled}
        ref={ref}
        {...props}
      >
        {icon ? (
          <>
            {isLoading ? (
              <Spinner>
                <LoaderCircle />
              </Spinner>
            ) : (
              icon
            )}
            <span>{children}</span>
          </>
        ) : (
          <>
            <span data-ignore={ignore}>{children}</span>
            <Spinner>{isLoading && <LoaderCircle />}</Spinner>
          </>
        )}
        {badge !== undefined && <MenuBadge>{badge}</MenuBadge>}
      </MenuButtonItemContainer>
    );
  })
)``;

export { MenuButtonItem };

export function MenuOverlay({
  trigger,
  content,
  right = false,
  bottom = false,
}: {
  trigger: OverlayTrigger;
  content: OverlayContent;
  right?: boolean;
  bottom?: boolean;
}) {
  return (
    <Overlay
      border
      trigger={trigger}
      position={{
        ...(bottom
          ? { bottom: ({ height }) => height + 4 }
          : { top: ({ height }) => height + 4 }),
        ...(right ? { right: () => 0 } : { left: () => 0 }),
      }}
      content={(p) => (
        <div
          style={{
            minWidth: "120px",
            padding: "0px",
            maxHeight: "50vh",
            overflowY: "auto",
          }}
        >
          <Menu>{content(p)}</Menu>
        </div>
      )}
    />
  );
}
